import { getConfig } from '@local/app-config';
import type { Span } from '@opentelemetry/api';
import { trace } from '@opentelemetry/api';
import { logs } from '@opentelemetry/api-logs';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
    BatchLogRecordProcessor,
    LoggerProvider,
    SimpleLogRecordProcessor,
    ConsoleLogRecordExporter,
} from '@opentelemetry/sdk-logs';
import type { LogRecordProcessor } from '@opentelemetry/sdk-logs';
import {
    WebTracerProvider,
    SimpleSpanProcessor,
    ConsoleSpanExporter,
} from '@opentelemetry/sdk-trace-web';
import type { SpanProcessor } from '@opentelemetry/sdk-trace-web';
import {
    SEMRESATTRS_DEPLOYMENT_ENVIRONMENT,
    SEMRESATTRS_SERVICE_NAME,
    SEMRESATTRS_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions';
import type { JSX } from 'react';

import { ATTRIBUTE_SESSION_ID, UNKNOWN } from './metrics.constants';
import type { OpenTelemetryConfig } from './metrics.types';

declare global {
    interface Window {
        otelTracer?: WebTracerProvider;
        otelRoot?: Span;
    }
}

export default function TraceProvider({
    children,
    openTelemetryConfig,
}: Readonly<{ children: JSX.Element; openTelemetryConfig: OpenTelemetryConfig }>) {
    const isProduction = (getConfig()?.environment ?? 'production') === 'production';
    const { name, version } = openTelemetryConfig;
    const hasConsole = openTelemetryConfig?.withConsoleExporter ?? false;
    const sessionId = window?.crypto?.randomUUID() ?? 'no-crypto';
    const exporterConfig: NonNullable<OpenTelemetryConfig['exporterConfig']> = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Basic ${openTelemetryConfig?.auth}`,
        },
        url: 'https://otel.elastic.seequent.com/v1/traces',
        ...openTelemetryConfig?.exporterConfig,
    };

    const resource = resourceFromAttributes({
        [SEMRESATTRS_SERVICE_NAME]: name,
        [SEMRESATTRS_SERVICE_VERSION]: version,
        [ATTRIBUTE_SESSION_ID]: sessionId,
        [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: getConfig()?.environment ?? UNKNOWN,
        ...openTelemetryConfig?.resourceAttributes,
    });

    // Create our OTLP trace & logs exporters
    const traceExporter = new OTLPTraceExporter(exporterConfig);
    const logExporter = new OTLPLogExporter({
        ...exporterConfig,
        url: 'https://otel.elastic.seequent.com/v1/logs',
    });

    const spanProcessors: SpanProcessor[] = [new SimpleSpanProcessor(traceExporter)];
    const logRecordProcessors: LogRecordProcessor[] = [new BatchLogRecordProcessor(logExporter)];

    // Console logger only available in environments other than production
    if (hasConsole && !isProduction) {
        spanProcessors.push(new SimpleSpanProcessor(new ConsoleSpanExporter()));
        logRecordProcessors.push(new SimpleLogRecordProcessor(new ConsoleLogRecordExporter()));
    }

    const provider = new WebTracerProvider({ resource, spanProcessors });
    const loggerProvider = new LoggerProvider({ resource, processors: logRecordProcessors });

    const instruments = [];
    const fetchInstrumentation = openTelemetryConfig?.fetchConfig
        ? new FetchInstrumentation({
              ...openTelemetryConfig?.fetchConfig,
          })
        : undefined;
    if (fetchInstrumentation) {
        fetchInstrumentation.setTracerProvider(provider);
        instruments.push(fetchInstrumentation);
    }
    const xhrInstrumentation = openTelemetryConfig?.xhrConfig
        ? new XMLHttpRequestInstrumentation({
              ...openTelemetryConfig?.xhrConfig,
          })
        : undefined;
    if (xhrInstrumentation) {
        xhrInstrumentation.setTracerProvider(provider);
        instruments.push(xhrInstrumentation);
    }

    const zoneManager = new ZoneContextManager();
    const propagator = new W3CTraceContextPropagator();
    provider.register({
        contextManager: zoneManager.enable(),
        propagator,
    });
    registerInstrumentations({
        instrumentations: instruments,
        tracerProvider: provider,
    });

    // Set the global singleton providers
    logs.setGlobalLoggerProvider(loggerProvider);
    trace.setGlobalTracerProvider(provider);

    if (!window?.otelTracer && provider) {
        window.otelTracer = provider;
    }

    return children;
}
